import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Notifications,
  NotificationsActive,
  CheckCircle,
  Cancel,
  Info,
  Warning,
  DoneAll,
  Delete,
  Refresh,
  Message,
  Email,
  Person,
  Group,
  School,
  AttachMoney,
  TrendingUp
} from '@mui/icons-material';
import {
  Box,
  Badge,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const NotificationBell = ({ socket }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const limit = 20;
  const menuRef = useRef(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
    loadUnreadMessages();

    // Écouter les nouvelles notifications via Socket.IO
    if (socket) {
      socket.on('new_notification', (notification) => {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
      });

      socket.on('notification_count', (data) => {
        setUnreadCount(data.count);
      });

      socket.on('new_message', () => {
        loadUnreadMessages();
      });

      return () => {
        socket.off('new_notification');
        socket.off('notification_count');
        socket.off('new_message');
      };
    }
  }, [socket]);

  const loadNotifications = async (reset = true) => {
    try {
      setLoading(true);
      const currentOffset = reset ? 0 : offset;
      const headers = getAuthHeaders();
      
      const res = await axios.get(
        `/api/notifications?limit=${limit}&offset=${currentOffset}`,
        { headers }
      );
      
      const newNotifications = res.data.notifications || [];
      setHasMore(res.data.hasMore || false);
      
      if (reset) {
        setNotifications(newNotifications);
        setOffset(limit);
      } else {
        setNotifications(prev => [...prev, ...newNotifications]);
        setOffset(prev => prev + limit);
      }
    } catch (err) {
      if (err.response?.status !== 404) console.error('Erreur chargement notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(
        '/api/notifications/unread/count',
        { headers }
      );
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      if (err.response?.status !== 404) console.error('Erreur compteur notifications:', err);
    }
  };

  const loadUnreadMessages = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(
        '/api/messages/unread/count',
        { headers }
      );
      setUnreadMessages(res.data.count || 0);
    } catch (err) {
      if (err.response?.status !== 404) console.error('Erreur compteur messages:', err);
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (unreadCount > 0) {
      markAllAsRead();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(
        `/api/notifications/${notificationId}/read`,
        {},
        { headers }
      );
      
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      if (err.response?.status !== 404) console.error('Erreur marquage comme lu:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(
        '/api/notifications/read/all',
        {},
        { headers }
      );
      
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (err) {
      if (err.response?.status !== 404) console.error('Erreur marquage tout comme lu:', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(
        `/api/notifications/${notificationId}`,
        { headers }
      );
      
      setNotifications(prev =>
        prev.filter(n => n._id !== notificationId)
      );
    } catch (err) {
      if (err.response?.status !== 404) console.error('Erreur suppression notification:', err);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'INSCRIPTION': <Person fontSize="small" />,
      'NOTE_PUBLIEE': <School fontSize="small" />,
      'PAIEMENT_RECU': <AttachMoney fontSize="small" />,
      'PRESENCE': <CheckCircle fontSize="small" />,
      'RAPPEL_COURS': <Notifications fontSize="small" />,
      'COMMUNICATION': <Message fontSize="small" />,
      'PASSATION': <TrendingUp fontSize="small" />,
      'ABSCENCE_JUSTIFIEE': <CheckCircle fontSize="small" />,
      'SYSTEME': <Info fontSize="small" />,
      'MESSAGE': <Message fontSize="small" />
    };
    return icons[type] || <Info fontSize="small" />;
  };

  const getNotificationColor = (type) => {
    const colors = {
      'INSCRIPTION': '#f59e0b',
      'NOTE_PUBLIEE': '#3b82f6',
      'PAIEMENT_RECU': '#10b981',
      'PRESENCE': '#10b981',
      'RAPPEL_COURS': '#ef4444',
      'COMMUNICATION': '#8b5cf6',
      'PASSATION': '#8b5cf6',
      'ABSCENCE_JUSTIFIEE': '#10b981',
      'SYSTEME': '#6b7280',
      'MESSAGE': '#8b5cf6'
    };
    return colors[type] || '#6b7280';
  };

  const getPriorityChip = (priority) => {
    const labels = {
      'BASSE': { label: 'Basse', color: '#6b7280' },
      'MOYENNE': { label: 'Moyenne', color: '#f59e0b' },
      'HAUTE': { label: 'Haute', color: '#ef4444' },
      'URGENTE': { label: 'Urgente', color: '#dc2626' }
    };
    const p = labels[priority] || labels['MOYENNE'];
    return (
      <Chip
        label={p.label}
        size="small"
        sx={{ bgcolor: alpha(p.color, 0.1), color: p.color, fontSize: '0.6rem', height: 20 }}
      />
    );
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadNotifications(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const totalUnread = unreadCount + unreadMessages;
  const messageNotifications = notifications.filter(n => n.type === 'MESSAGE');

  return (
    <>
      <Tooltip title={`Notifications${totalUnread > 0 ? ` (${totalUnread} non lues)` : ''}`}>
        <IconButton
          onClick={handleOpen}
          sx={{ color: '#6b7280' }}
          aria-label="notifications"
        >
          <Badge
            badgeContent={totalUnread}
            color="error"
            max={99}
          >
            {totalUnread > 0 ? <NotificationsActive /> : <Notifications />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        ref={menuRef}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 500,
            borderRadius: 2,
            mt: 1.5,
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              🔔 Notifications
              {totalUnread > 0 && (
                <Chip
                  label={`${totalUnread} non lue${totalUnread > 1 ? 's' : ''}`}
                  size="small"
                  sx={{ ml: 1, bgcolor: '#dbeafe', color: '#1e40af' }}
                />
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {notifications.some(n => !n.read) && (
                <Tooltip title="Tout marquer comme lu">
                  <IconButton size="small" onClick={markAllAsRead}>
                    <DoneAll fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Actualiser">
                <IconButton size="small" onClick={() => loadNotifications(true)}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ mt: 1, minHeight: 36 }}
          >
            <Tab
              label={
                <Badge badgeContent={unreadCount} color="error" max={9}>
                  📢 Toutes
                </Badge>
              }
              sx={{ minHeight: 36, fontSize: '0.75rem' }}
            />
            <Tab
              label={
                <Badge badgeContent={unreadMessages} color="error" max={9}>
                  💬 Messages
                </Badge>
              }
              sx={{ minHeight: 36, fontSize: '0.75rem' }}
            />
          </Tabs>
        </Box>

        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Notifications sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
              <Typography color="text.secondary">Aucune notification</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {(tabValue === 0 ? notifications : messageNotifications).map((notification) => (
                <ListItem
                  key={notification._id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #f3f4f6',
                    bgcolor: notification.read ? 'transparent' : alpha('#dbeafe', 0.2),
                    '&:hover': {
                      bgcolor: alpha('#f3f4f6', 0.5)
                    },
                    cursor: notification.actionUrl ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (notification.actionUrl) {
                      navigate(notification.actionUrl);
                    }
                    if (!notification.read) {
                      markAsRead(notification._id);
                    }
                    handleClose();
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(getNotificationColor(notification.type), 0.1)
                    }}>
                      {getNotificationIcon(notification.type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="body2" fontWeight={notification.read ? 400 : 600} noWrap>
                          {notification.title}
                        </Typography>
                        {getPriorityChip(notification.priority)}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true, locale: fr })}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification._id);
                    }}
                    sx={{ color: '#9ca3af' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {hasMore && !loading && notifications.length > 0 && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Button size="small" onClick={handleLoadMore}>
                Voir plus
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 1, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 1 }}>
          <Button
            size="small"
            color="primary"
            fullWidth
            onClick={() => {
              handleClose();
              navigate('/messages');
            }}
            startIcon={<Message />}
          >
            Messages
          </Button>
          {unreadMessages > 0 && (
            <Chip
              label={`${unreadMessages} non lus`}
              size="small"
              sx={{ bgcolor: '#dbeafe', color: '#1e40af' }}
            />
          )}
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;


